/**
 * landing.js v3 - Strider template skin, our brain.
 *
 * Combines the purchased template's behaviors (loader, +18 age gate,
 * cookie bar, navbar, media-carousel progress) with our systems:
 * AR/EN i18n, game-sprite decorations, the fullscreen mobile game
 * launcher, the difficulty preview, and the trailer overlay.
 * The game itself (play.html) stays untouched.
 */
import { buildSprites } from '../render/Sprites.js';
import { getLang, setLang } from '../ui/i18n.js';

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ================= i18n ================= */

const I18N = {
  title: { ar: 'Chicken Road | جرّب الديمو مجاناً', en: 'Chicken Road | Try the free demo' },
  metaDesc: {
    ar: 'لعبة Chicken Road: اعبر بالدجاجة خطاً بعد خط، كل خط يرفع مضاعفك، واسحب أرباحك قبل مرور السيارة. جرّب النسخة التجريبية مجاناً من دون تسجيل.',
    en: 'Chicken Road: cross lane by lane, every lane raises your multiplier, and cash out before a car gets there. Try the demo free, no sign-up.',
  },
  loading: { ar: 'جارٍ التحميل', en: 'Loading' },
  loadingRoad: { ar: 'جارٍ تجهيز الطريق…', en: 'Preparing the road…' },

  ageTitle: { ar: 'التحقق من العمر (+18)', en: 'Age Verification (18+)' },
  ageLabel: { ar: 'أدخل سنة ميلادك', en: 'Enter your birth year' },
  ageVerify: { ar: 'تحقق', en: 'Verify' },
  ageInvalid: { ar: 'الرجاء إدخال سنة صحيحة.', en: 'Please enter a valid year.' },
  ageDenied: { ar: 'عذراً، هذا الموقع للبالغين فوق 18 عاماً فقط.', en: 'Sorry, this site is for adults 18+ only.' },

  cookieText: { ar: 'نستخدم ملفات تعريف الارتباط لتحسين تجربتك.', en: 'We use cookies for a better user experience.' },
  cookiePolicy: { ar: 'سياسة الخصوصية', en: 'Privacy Policy' },
  cookieAccept: { ar: 'قبول الكل', en: 'Accept all' },
  cookieReject: { ar: 'رفض الكل', en: 'Reject all' },

  navGame: { ar: 'اللعبة', en: 'The game' },
  navFeatures: { ar: 'المميزات', en: 'Features' },
  navLevels: { ar: 'المستويات', en: 'Levels' },
  navFaq: { ar: 'أسئلة', en: 'FAQ' },
  ctaReal: { ar: 'العب بمال حقيقي', en: 'Play for real money' },
  ctaDemo: { ar: 'جرّب الديمو مجاناً', en: 'Try the free demo' },

  heroTitle: {
    ar: 'اعبر الطريق…<br />واسحب قبل وصول السيارة',
    en: 'Cross the road…<br />cash out before the car arrives',
  },
  watchTrailer: { ar: 'شاهد الإعلان', en: 'Watch the trailer' },
  chipFree: { ar: 'ديمو مجاني', en: 'Free demo' },
  chipNoReg: { ar: 'بدون تسجيل', en: 'No sign-up' },
  chipLevels: { ar: 'أربعة مستويات', en: 'Four levels' },
  chipCash: { ar: 'سحب في أي لحظة', en: 'Cash out any time' },

  demoTitle: { ar: 'جرّب اللعبة الآن، من دون تسجيل', en: 'Play the demo now, no sign-up' },
  demoSub: {
    ar: 'رصيد تجريبي بقيمة 10,000$ يتجدد تلقائياً. اللعبة نفسها والأرقام نفسها، من دون أي مخاطرة.',
    en: 'A 10,000$ practice balance that refills itself. Same game, same numbers, zero risk.',
  },
  liveDemo: { ar: 'ديمو مباشر', en: 'Live demo' },
  tagBalance: { ar: 'رصيد تجريبي', en: 'Practice balance' },
  demoFullscreen: { ar: 'افتح اللعبة بملء الشاشة', en: 'Open the game full screen' },
  iframeTitle: { ar: 'لعبة Chicken Road التجريبية', en: 'Chicken Road demo game' },
  playNow: { ar: 'ابدأ اللعب', en: 'Start playing' },
  glNote: { ar: 'تفتح اللعبة بملء الشاشة', en: 'Opens full screen' },
  back: { ar: 'رجوع', en: 'Back' },
  rotateHint: { ar: 'لفّ جهازك للوضع الأفقي لتجربة أوسع', en: 'Rotate your device for a wider view' },

  why1t: { ar: 'قرارك يصنع النتيجة', en: 'Your decision shapes the round' },
  why1p: {
    ar: 'ليست لعبة تكتفي بمشاهدتها؛ كل خطوة قرار: إما أن تتقدّم فترفع المضاعف، أو تسحب فتثبّت ربحك.',
    en: 'Not a slot you watch. Every step is a call: push on and raise the multiplier, or cash out and lock it.',
  },
  why2t: { ar: 'سحب في أي لحظة', en: 'Cash out any moment' },
  why2p: {
    ar: 'زر السحب بين يديك طوال الجولة، ولحظة الضغط عليه تحصل فوراً على رهانك مضروباً بالمضاعف الحالي.',
    en: 'The cash-out button is yours the whole round. The moment you press it, you take your bet times the current multiplier.',
  },
  why3t: { ar: 'شفافية رياضية', en: 'Mathematical transparency' },
  why3p: {
    ar: 'لكل جولة بصمة مشفّرة (SHA-256) تُحسب قبل انطلاقها، لتتأكد بنفسك أن النتيجة لم تتغيّر بعد رهانك. العائد النظري 98% ومعلن في المعادلات.',
    en: 'Every round gets a SHA-256 fingerprint computed before it starts, so you can verify nothing changed after your bet. Theoretical return is a stated 98%.',
  },

  levelsTitle: { ar: 'اختر مستوى المخاطرة', en: 'Pick your risk level' },
  levelsSub: {
    ar: 'كلما زادت السيارات كبرت المضاعفات. اضغط على أي مستوى لمعاينة طريقه.',
    en: 'More cars means bigger multipliers. Tap a level to preview its road.',
  },
  levelsAria: { ar: 'مستويات الصعوبة', en: 'Difficulty levels' },
  lvEasy: { ar: 'سهل', en: 'Easy' },
  lvMedium: { ar: 'متوسط', en: 'Medium' },
  lvHard: { ar: 'صعب', en: 'Hard' },
  lvHardcore: { ar: 'هاردكور', en: 'Hardcore' },
  lvSteps: { ar: 'الخطوات', en: 'Steps' },
  lvCars: { ar: 'السيارات', en: 'Cars' },
  cars1: { ar: '1 من 25', en: '1 of 25' },
  cars3: { ar: '3 من 25', en: '3 of 25' },
  cars5: { ar: '5 من 25', en: '5 of 25' },
  cars10: { ar: '10 من 25', en: '10 of 25' },
  risk1: { ar: 'مخاطرة منخفضة', en: 'Low risk' },
  risk2: { ar: 'مخاطرة متوسطة', en: 'Medium risk' },
  risk3: { ar: 'مخاطرة عالية', en: 'High risk' },
  risk4: { ar: 'أعلى مخاطرة', en: 'Maximum risk' },
  previewLabel: { ar: 'السيارات المخفية على الطريق:', en: 'Hidden cars on the road:' },
  thLevel: { ar: 'المستوى', en: 'Level' },
  thSteps: { ar: 'الخطوات', en: 'Steps' },
  thCars: { ar: 'السيارات', en: 'Cars' },
  thMax: { ar: 'أقصى مضاعف', en: 'Max multiplier' },
  levelsNote: {
    ar: 'الأرقام الكبيرة سقف نظري نادر جداً، وليست هدفاً لكل جولة.',
    en: 'The big numbers are a rare theoretical ceiling, not a per-round target.',
  },

  faqTitle: { ar: 'أسئلة شائعة', en: 'Quick questions' },
  q1: { ar: 'هل النسخة التجريبية مجانية فعلاً؟', en: 'Is the demo really free?' },
  a1: {
    ar: 'نعم. رصيد تجريبي يتجدد تلقائياً، من دون تسجيل أو أي بيانات.',
    en: 'Yes. A practice balance that refills itself, no sign-up and no details.',
  },
  q2: { ar: 'ما الفرق بين التجريبي واللعب الحقيقي؟', en: 'Demo vs real play, what changes?' },
  a2: {
    ar: 'اللعبة نفسها والأرقام نفسها. الفرق أن اللعب الحقيقي يجري بمال فعلي على منصة الشريك ويتطلب إنشاء حساب هناك.',
    en: 'Same game, same numbers. Real play happens with real money on the partner platform and requires an account there.',
  },
  q3: { ar: 'هل توجد استراتيجية مضمونة؟', en: 'Is there a guaranteed strategy?' },
  a3: {
    ar: 'لا. اللعبة عشوائية والهامش قائم في المعادلات مهما فعلت. حدّد ميزانية والتزم بها، وعامل أي ربح كمكافأة لا كخطة.',
    en: 'No. The game is random and the house edge is in the math no matter what. Set a budget, stick to it, and treat any win as a bonus, not a plan.',
  },
  q4: { ar: 'هل تعمل على الجوال؟', en: 'Does it work on mobile?' },
  a4: {
    ar: 'نعم، اللعبة والموقع مصمّمان للجوال والحاسوب معاً. ولتجربة أوسع افتح اللعبة بملء الشاشة.',
    en: 'Yes, the game and the site are built for phones and desktops. For a bigger view, open the game full screen.',
  },
  q5: { ar: 'كيف ألعب بمسؤولية؟', en: 'How do I play responsibly?' },
  a5: {
    ar: 'حدّد ميزانية قبل البدء وتوقّف عند بلوغها، وتعامل مع اللعب كترفيه لا كمصدر دخل. اللعب الحقيقي للبالغين فقط (+18).',
    en: 'Set a budget before you start and stop when you reach it. Treat play as entertainment, not income. Real play is for adults only (18+).',
  },

  ctaTitle: { ar: 'جاهز لعبور الطريق؟', en: 'Ready to cross the road?' },
  ctaNote: {
    ar: 'اللعب بمال حقيقي للبالغين فقط. حدّد ميزانيتك مسبقاً والعب بمسؤولية.',
    en: 'Real-money play is for adults only. Set a budget in advance and play responsibly.',
  },

  footerPrivacy: { ar: 'سياسة الخصوصية', en: 'Privacy Policy' },
  footerTerms: { ar: 'الشروط والأحكام', en: 'Terms & Conditions' },
  footerMain: {
    ar: 'القمار للبالغين فقط وقد يؤدي إلى خسارة أموالك. العب بمسؤولية، حدّد ميزانيتك مسبقاً، وتوقف عند بلوغها.',
    en: 'Gambling is for adults only and can lose you money. Play responsibly, set a budget in advance, and stop when you reach it.',
  },
  copyright: { ar: '© Chicken Road', en: '© Chicken Road' },
};

function applyLang(lang) {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  for (const [key, val] of Object.entries(I18N)) {
    document.querySelectorAll(`[data-i18n="${key}"]`).forEach((n) => (n.innerHTML = val[lang]));
    document.querySelectorAll(`[data-i18n-content="${key}"]`).forEach((n) => n.setAttribute('content', val[lang]));
    document.querySelectorAll(`[data-i18n-attr-aria-label="${key}"]`).forEach((n) => n.setAttribute('aria-label', val[lang]));
  }
  document.title = I18N.title[lang];
  document.getElementById('demo-iframe')?.setAttribute('title', I18N.iframeTitle[lang]);
  document.querySelectorAll('.lang-btn').forEach((b) => (b.textContent = lang === 'ar' ? 'EN' : 'عربي'));

  // the hero background teaser exists in both languages
  const bg = document.getElementById('bgvid');
  if (bg) {
    const want = lang === 'ar' ? '/media/trailer-ar.mp4' : '/media/trailer-en.mp4';
    const source = bg.querySelector('source');
    if (source && !source.src.endsWith(want)) {
      source.src = want;
      bg.load();
      bg.play().catch(() => {});
    }
  }
  syncPreview();
}

function toggleLang() {
  const next = getLang() === 'ar' ? 'en' : 'ar';
  setLang(next);
  applyLang(next);
  const frame = document.getElementById('demo-iframe');
  if (frame && frame.getAttribute('src')) frame.src = frame.getAttribute('src');
}

/* ================= sprites into the DOM ================= */

const sprites = buildSprites();
{
  const urls = {};
  const MAP = { chicken: sprites.chicken, coin: sprites.coin, manhole: sprites.manhole, barrier: sprites.barrier };
  document.querySelectorAll('[data-sprite]').forEach((img) => {
    const key = img.dataset.sprite;
    if (!MAP[key]) return;
    urls[key] ||= MAP[key].toDataURL();
    img.src = urls[key];
  });
}

/* ================= template: storage helpers (from Strider) ================= */

function setStorage(key, value, days = 30) {
  try {
    localStorage.setItem(key, JSON.stringify({ value, expiry: Date.now() + days * 864e5 }));
  } catch { /* storage unavailable: the gate simply re-asks next visit */ }
}
function getStorage(key) {
  try {
    const data = localStorage.getItem(key);
    if (!data) return null;
    const parsed = JSON.parse(data);
    if (Date.now() > parsed.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}

/* ================= template: age gate (+18) ================= */

{
  const wrapper = document.getElementById('age-checker-wrapper');
  const overlay = document.getElementById('age-checker-overlay');
  const hide = () => {
    wrapper.style.display = 'none';
    overlay.style.display = 'none';
  };
  if (getStorage('ageVerified') === 'true') {
    hide();
  } else {
    document.getElementById('age-form').addEventListener('submit', (e) => {
      e.preventDefault();
      const lang = getLang();
      const birthYear = parseInt(document.getElementById('birthYear').value, 10);
      const currentYear = new Date().getFullYear();
      const result = document.getElementById('result');
      if (isNaN(birthYear) || birthYear < 1900 || birthYear > currentYear) {
        result.innerHTML = `<span class='text-danger'>${I18N.ageInvalid[lang]}</span>`;
        return;
      }
      if (currentYear - birthYear >= 18) {
        setStorage('ageVerified', 'true', 30);
        hide();
      } else {
        result.innerHTML = `<span class='text-danger'>${I18N.ageDenied[lang]}</span>`;
      }
    });
  }
}

/* ================= template: cookie bar ================= */

{
  const bar = document.getElementById('policy-bar');
  const close = () => bar.classList.remove('open');
  if (!getStorage('cookieChoice')) bar.classList.add('open');
  document.getElementById('accept-button')?.addEventListener('click', () => {
    setStorage('cookieChoice', 'accepted', 180);
    close();
  });
  document.getElementById('reject-button')?.addEventListener('click', () => {
    setStorage('cookieChoice', 'rejected', 180);
    close();
  });
  document.getElementById('close-policy-bar')?.addEventListener('click', close);
}

/* ================= template: navbar + loader + year ================= */

document.getElementById('hamburger')?.addEventListener('click', function () {
  this.classList.toggle('open');
});

window.addEventListener('scroll', () => {
  const navbar = document.querySelector('.navbar-dark');
  navbar?.classList.toggle('scrolled', window.scrollY > window.innerHeight * 0.85);
}, { passive: true });

window.addEventListener('load', () => {
  const progress = document.getElementById('progress');
  const loaderWrapper = document.getElementById('loader-wrapper');
  if (!progress || !loaderWrapper) return;
  progress.style.transition = 'width 0.3s linear';
  progress.style.width = '100%';
  progress.addEventListener('transitionend', function handler() {
    loaderWrapper.classList.add('content-loaded');
    progress.removeEventListener('transitionend', handler);
  });
  // safety: never trap the page behind the loader
  setTimeout(() => loaderWrapper.classList.add('content-loaded'), 2500);
});

const yearEl = document.getElementById('year');
if (yearEl) yearEl.textContent = new Date().getFullYear();

/* ================= template: media carousel progress ================= */

window.addEventListener('load', () => {
  const carousel = document.getElementById('media-carousel');
  const progressBar = document.querySelector('.carousel-progress .progress-bar');
  if (!carousel || !progressBar || !window.bootstrap) return;
  const interval = 6000;
  const startProgressBar = () => {
    progressBar.style.transition = 'none';
    progressBar.style.width = '0%';
    setTimeout(() => {
      progressBar.style.transition = `width ${interval}ms linear`;
      progressBar.style.width = '100%';
    }, 10);
  };
  new window.bootstrap.Carousel(carousel, { interval, ride: 'carousel', pause: false });
  carousel.addEventListener('slide.bs.carousel', startProgressBar);
  startProgressBar();
});

/* ================= trailer overlay ================= */

{
  let overlay = null;
  const close = () => {
    overlay?.remove();
    overlay = null;
  };
  document.getElementById('trailer-btn')?.addEventListener('click', () => {
    if (overlay) return;
    const lang = getLang();
    const src = lang === 'ar' ? '/media/trailer-ar.mp4' : '/media/trailer-en.mp4';
    overlay = document.createElement('div');
    overlay.className = 'video-overlay';
    overlay.innerHTML = `
      <video src="${src}" controls autoplay playsinline></video>
      <button class="video-close" aria-label="${I18N.back[lang]}">✕</button>`;
    document.body.appendChild(overlay);
    overlay.querySelector('.video-close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
  });
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}

/* ================= inline game (desktop) ================= */

const isPhone = () => window.matchMedia('(max-width: 860px)').matches;

{
  const frame = document.getElementById('demo-iframe');
  const loader = document.getElementById('iframe-loader');
  frame.addEventListener('load', () => (loader.style.display = 'none'));
  const loadInline = () => {
    if (!isPhone() && !frame.getAttribute('src')) frame.src = frame.dataset.src;
  };
  loadInline();
  window.addEventListener('resize', loadInline);
}

/* ================= fullscreen game overlay (mobile) ================= */

let gameOverlay = null;

function closeGame() {
  if (!gameOverlay) return;
  gameOverlay.remove();
  gameOverlay = null;
  document.documentElement.style.overflow = '';
}

function openGame() {
  if (gameOverlay) return;
  const lang = getLang();
  gameOverlay = document.createElement('div');
  gameOverlay.className = 'game-overlay';
  gameOverlay.innerHTML = `
    <iframe src="/play.html" title="${I18N.iframeTitle[lang]}" allow="autoplay"></iframe>
    <button class="game-back">✕ <span>${I18N.back[lang]}</span></button>
    <div class="rotate-hint">${I18N.rotateHint[lang]}</div>`;
  document.body.appendChild(gameOverlay);
  document.documentElement.style.overflow = 'hidden';
  history.pushState({ game: 1 }, '');
  gameOverlay.querySelector('.game-back').addEventListener('click', () => history.back());
  const hint = gameOverlay.querySelector('.rotate-hint');
  if (window.matchMedia('(orientation: portrait)').matches) {
    setTimeout(() => hint.classList.add('gone'), 3800);
  } else {
    hint.remove();
  }
}

window.addEventListener('popstate', closeGame);
document.getElementById('game-launch')?.addEventListener('click', openGame);

/* ================= difficulty selector ================= */

const previewSlots = document.getElementById('preview-slots');
const previewName = document.getElementById('preview-name');
const previewCount = document.getElementById('preview-count');
let selectedLevel = 'easy';

function renderSlots(k) {
  const total = 25;
  const carIdx = new Set();
  while (carIdx.size < k) carIdx.add((Math.random() * total) | 0);
  previewSlots.innerHTML = Array.from({ length: total }, (_, i) =>
    `<i class="${carIdx.has(i) ? 'car' : ''}"></i>`
  ).join('');
  previewCount.textContent = `${k} / ${total}`;
}

function syncPreview() {
  const lang = getLang();
  const key = { easy: 'lvEasy', medium: 'lvMedium', hard: 'lvHard', hardcore: 'lvHardcore' }[selectedLevel];
  if (previewName && I18N[key]) previewName.textContent = I18N[key][lang];
}

document.querySelectorAll('.level-card').forEach((card) =>
  card.addEventListener('click', () => {
    document.querySelectorAll('.level-card').forEach((c) => c.setAttribute('aria-pressed', 'false'));
    card.setAttribute('aria-pressed', 'true');
    selectedLevel = card.dataset.level;
    renderSlots(Number(card.dataset.k));
    syncPreview();
  })
);
renderSlots(1);

/* ================= language toggles ================= */

applyLang(getLang());
document.getElementById('lang-toggle')?.addEventListener('click', toggleLang);
document.getElementById('lang-toggle-2')?.addEventListener('click', toggleLang);
